import * as React from 'react';
import { Outlet } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  Sheet,
  Stack,
  Typography,
} from '@mui/joy';

import {
  CongregationAccessProvider,
  type CongregationAccessContextValue,
} from '../../congregation/context/CongregationAccessContext';
import { fetchCongregationMe, checkCongregationAccess } from '../../api';
import type { CongregationMembership } from '../../types/congregation';
import { useSnackbarStore } from '../../store/useSnackbarStore';

interface LoadingState {
  status: 'loading';
}

interface ReadyState {
  status: 'ready';
  membership: CongregationMembership;
  isFacilitator: boolean;
  facilitatorBatches: number[];
}

interface DeniedState {
  status: 'denied';
  message: string;
}

type AccessState = LoadingState | ReadyState | DeniedState;

const denialSteps = [
  'Confirm that you are signed in with your temple administrator account.',
  'Request congregation admin or facilitator access from the Congregation Operations lead.',
  'If access was recently granted, refresh and try again after a few minutes.',
];

const escalateEmail = 'mailto:tech@iskconkanpur.org?subject=Congregation%20admin%20access%20request';

const hasAdminRole = (membership: CongregationMembership) =>
  membership.roles?.some((role) => role.role === 'ADMIN' && !role.revokedAt) ?? false;

const initialState: LoadingState = { status: 'loading' };

export default function CongregationAdminLayout() {
  const [state, setState] = React.useState<AccessState>(initialState);
  const { showSnackbar } = useSnackbarStore();

  const refresh = React.useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const membership = await fetchCongregationMe();
      
      // Check if admin or facilitator
      const accessCheck = await checkCongregationAccess();
      if (!accessCheck.canAccess) {
        setState({
          status: 'denied',
          message: 'Your account does not have congregation admin or facilitator permissions.',
        });
        return;
      }
      
      setState({ 
        status: 'ready', 
        membership,
        isFacilitator: accessCheck.isFacilitator,
        facilitatorBatches: accessCheck.facilitatorBatches,
      });
    } catch (error) {
      console.error('Failed to load congregation membership', error);
      showSnackbar('Unable to verify congregation access. Please try again.', 'danger');
      setState({
        status: 'denied',
        message: 'We could not confirm your congregation admin access.',
      });
    }
  }, [showSnackbar]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const membership = state.status === 'ready' ? state.membership : undefined;
  const providerValue: CongregationAccessContextValue | null = React.useMemo(
    () =>
      membership
        ? {
            membership,
            isAdmin: true,
            refresh,
          }
        : null,
    [membership, refresh],
  );

  if (state.status === 'loading') {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size="md" aria-label="Checking congregation access" />
          <Typography level="title-md">Verifying congregation admin access…</Typography>
        </Stack>
      </Box>
    );
  }

  if (state.status === 'denied') {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <Sheet
          variant="outlined"
          sx={{
            p: 4,
            maxWidth: 560,
            borderRadius: 'xl',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography level="h4">Congregation access required</Typography>
          <Typography level="body-md">{state.message}</Typography>
          <Typography level="title-sm">How to get access</Typography>
          <List marker="decimal" sx={{ '--ListItem-minHeight': 'unset' }}>
            {denialSteps.map((step) => (
              <ListItem key={step} sx={{ pl: 0 }}>
                <Typography level="body-sm">{step}</Typography>
              </ListItem>
            ))}
          </List>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1 }}>
            <Button variant="solid" color="primary" onClick={refresh}>
              Retry access check
            </Button>
            <Button
              variant="outlined"
              color="neutral"
              component="a"
              href={escalateEmail}
            >
              Contact support
            </Button>
          </Stack>
        </Sheet>
      </Box>
    );
  }

  if (!providerValue) {
    return null;
  }

  return (
    <CongregationAccessProvider value={providerValue}>
      <Outlet />
    </CongregationAccessProvider>
  );
}
